/**
 * Presentazione della biblioteca `kb_contents` in console: toni dei chip e
 * frasi che spiegano un comportamento.
 *
 * ⚠️ Le ETICHETTE dei vocabolari NON stanno qui: `KB_FORMATO_LABEL`,
 * `KB_STATO_LABEL`, `KB_FRESCHEZZA_LABEL`, `KB_LIVELLO_LABEL` e
 * `KB_TIPO_MODIFICA_LABEL` vivono in `shared/kb/kbContentModel.ts` e si
 * importano da lì. Se la console riscrivesse le parole, l'editore leggerebbe
 * «correzione di forma» dove l'app scrive qualcos'altro.
 *
 * Qui vive solo ciò che è puramente visivo (il tono di un chip non ha senso in
 * un modulo condiviso con le Cloud Functions) e le spiegazioni rivolte a chi
 * scrive, che nell'app non compaiono mai.
 */

import type { SoftChipTone } from '../SoftChip';
import type {
  KbFormato,
  KbFreschezza,
  KbLivelloImpatto,
  KbStato,
} from '@shared/kb/kbTypes';

// ============================================================================
// TONI
// ============================================================================

export const KB_STATO_TONE: Record<KbStato, SoftChipTone> = {
  bozza: 'default',
  in_revisione: 'info',
  pubblicato: 'success',
  archiviato: 'default',
};

export const KB_FRESCHEZZA_TONE: Record<KbFreschezza, SoftChipTone> = {
  attuale: 'success',
  da_aggiornare: 'warning',
  obsoleto: 'default',
};

/** Il peso dell'annuncio si legge dal colore prima che dalla parola. */
export const KB_LIVELLO_TONE: Record<KbLivelloImpatto, SoftChipTone> = {
  informativo: 'default',
  rilevante: 'info',
  importante: 'warning',
  critico: 'error',
};

export const KB_FORMATO_TONE: Record<KbFormato, SoftChipTone> = {
  articolo: 'primary',
  video: 'info',
  novita: 'gold',
};

// ============================================================================
// SPIEGAZIONI PER CHI SCRIVE
// ============================================================================

export const KB_STATO_HINT: Record<KbStato, string> = {
  bozza: 'nessuno la vede, si può salvare a metà',
  in_revisione: 'in attesa di controllo, ancora non visibile',
  pubblicato: 'visibile ai destinatari',
  archiviato: 'ritirato: resta lo storico, non si mostra più',
};

export const KB_FRESCHEZZA_HINT: Record<KbFreschezza, string> = {
  attuale: 'allineato al gestionale di oggi',
  da_aggiornare: 'si legge comunque, con un avviso in cima',
  obsoleto: 'mai mostrato a nessuno',
};

/**
 * La differenza fra i due campi capability è la decisione di prodotto del
 * 25/07/2026 e va scritta accanto ai campi, non nascosta in un doc: una svista
 * qui nasconde la documentazione del gestionale a un cliente che l'ha pagato.
 */
export const KB_HINT_CAPABILITY_INFORMATIVA =
  'NON nasconde niente: il contenuto si legge da tutte le aziende e a chi non ha ' +
  'questa funzione la UI aggiunge una nota tipo «disponibile nel piano Business». ' +
  'È la scelta normale per la documentazione del gestionale.';

export const KB_HINT_CAPABILITY_RICHIESTA =
  'NASCONDE il contenuto a chi non ha questa funzione. Uso eccezionale (una beta ' +
  'riservata): non è un confine di sicurezza, chi conosce l\'indirizzo lo legge ' +
  'comunque. Per documentazione davvero riservata a un\'azienda serve un ' +
  'contenuto di quell\'azienda, non questo campo.';

export const KB_HINT_AREE_COPERTE =
  'Vocabolario controllato: è la giunzione con i rilasci. Quando una di queste ' +
  'aree cambierà, il sistema proporrà di rivedere questo contenuto.';

export const KB_HINT_INDIRIZZO =
  'Diventa l\'indirizzo del contenuto e non si cambia più: è ciò che le persone ' +
  'si incollano in chat. Minuscole, cifre e trattini.';

export const KB_HINT_ROTTA_AZIONE =
  'Il salto dalla novità all\'uso reale: senza destinazione il bottone non si mostra.';

// ============================================================================
// FORMATTAZIONE
// ============================================================================

/** Data leggibile in italiano; '—' se l'istante non c'è. */
export function formattaData(millis: number | null): string {
  if (millis == null) return '—';
  return new Date(millis).toLocaleDateString('it-IT', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

/** `Date` → valore per un `<input type="date">` (fuso locale, non UTC). */
export function aValoreInputData(millis: number | null): string {
  if (millis == null) return '';
  const d = new Date(millis);
  const mese = String(d.getMonth() + 1).padStart(2, '0');
  const giorno = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mese}-${giorno}`;
}

/**
 * Valore di un `<input type="date">` → millis a mezzanotte LOCALE.
 * `null` se il campo è vuoto o la data non è interpretabile: mai «adesso» di
 * nascosto, che produrrebbe scadenze sbagliate senza che nessuno se ne accorga.
 */
export function daValoreInputData(valore: string): number | null {
  const parti = valore.split('-');
  if (parti.length !== 3) return null;
  const anno = Number(parti[0]);
  const mese = Number(parti[1]);
  const giorno = Number(parti[2]);
  if (!Number.isFinite(anno) || !Number.isFinite(mese) || !Number.isFinite(giorno)) return null;
  const d = new Date(anno, mese - 1, giorno, 0, 0, 0, 0);
  const ms = d.getTime();
  return Number.isFinite(ms) ? ms : null;
}

/** Etichetta leggibile di un id snake_case (`commerciale_interno` → «commerciale interno»). */
export function etichettaDaId(id: string): string {
  return id.replace(/_/g, ' ');
}
